import { Link2, UserPlus, X } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';

import { Button } from '@/src/components/Button';
import { useToast } from '@/src/components/Toast';
import {
  dismissInviteEducation,
  getMyInviteLink,
} from '@/src/features/invites/invite-service';
import { trackEvent } from '@/src/lib/analytics';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { copyTextToClipboard } from '@/src/utils/clipboard';

export function InviteEducationModal({
  onDismissed,
  uid,
  visible,
}: {
  onDismissed: () => void;
  uid: string | undefined;
  visible: boolean;
}) {
  const { showError, showSuccess } = useToast();
  const [copying, setCopying] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [inviteLinkURL, setInviteLinkURL] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    if (!visible || !uid) {
      setInviteLinkURL(null);
      return undefined;
    }

    trackEvent('invite_education_view');

    getMyInviteLink()
      .then((invite) => {
        if (active) {
          setInviteLinkURL(invite.url);
        }
      })
      .catch(() => {
        if (active) {
          setInviteLinkURL(null);
        }
      });

    return () => {
      active = false;
    };
  }, [uid, visible]);

  async function markDismissed() {
    if (!uid) {
      onDismissed();
      return;
    }

    setDismissing(true);
    onDismissed();
    trackEvent('invite_education_dismiss');

    try {
      await dismissInviteEducation(uid);
    } catch (error) {
      showError(error, 'Unable to save that invite popup preference.');
    } finally {
      setDismissing(false);
    }
  }

  async function copyInviteLink() {
    if (!uid) {
      return;
    }

    setCopying(true);
    trackEvent('invite_link_copy_start', { source: 'invite_education' });

    try {
      const inviteURL = inviteLinkURL ?? (await getMyInviteLink()).url;

      if (!inviteLinkURL) {
        setInviteLinkURL(inviteURL);
      }

      await copyTextToClipboard(inviteURL);
      trackEvent('invite_link_copy', { source: 'invite_education' });
      showSuccess('Invite link copied.', 'Ready to share');
      await markDismissed();
    } catch (error) {
      showError(error, 'Unable to copy your invite link.');
    } finally {
      setCopying(false);
    }
  }

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={() => {
        void markDismissed();
      }}
    >
      <Pressable
        accessibilityRole="button"
        style={styles.backdrop}
        onPress={() => {
          void markDismissed();
        }}
      >
        <Pressable
          accessibilityRole="none"
          style={styles.card}
          onPress={(event) => event.stopPropagation()}
        >
          <View style={styles.header}>
            <View style={styles.iconFrame}>
              <UserPlus color={colors.primary} size={24} strokeWidth={2.5} />
            </View>
            <Pressable
              accessibilityLabel="Dismiss invite popup"
              accessibilityRole="button"
              disabled={dismissing || copying}
              onPress={() => {
                void markDismissed();
              }}
              style={styles.closeButton}
            >
              <X color={colors.mutedText} size={20} strokeWidth={2.5} />
            </Pressable>
          </View>
          <Text style={styles.title}>Invite friends to Frienzzi</Text>
          <Text style={styles.body}>
            Copy your personal invite link and send it to friends. When someone
            creates a profile from your link, they count toward your Invites and
            you both follow each other automatically.
          </Text>
          <View style={styles.actions}>
            <Button
              title="Copy invite link"
              loading={copying}
              disabled={dismissing}
              onPress={() => {
                void copyInviteLink();
              }}
              style={styles.actionButton}
            />
            <Button
              title="Dismiss"
              variant="secondary"
              loading={dismissing && !copying}
              disabled={copying}
              onPress={() => {
                void markDismissed();
              }}
              style={styles.actionButton}
            />
          </View>
          <View style={styles.footerRow}>
            <Link2 color={colors.mutedText} size={14} strokeWidth={2.4} />
            <Text style={styles.footerText}>
              Your invite link stays the same.
            </Text>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(29, 15, 70, 0.42)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.xl,
  },
  card: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    maxWidth: 420,
    padding: spacing.xl,
    width: '100%',
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  iconFrame: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  closeButton: {
    alignItems: 'center',
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  title: {
    color: colors.text,
    fontSize: 24,
    fontWeight: '900',
  },
  body: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  actionButton: {
    flexGrow: 1,
  },
  footerRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
  },
  footerText: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '700',
  },
});
